import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth token — persisted in localStorage so it survives page reloads and PWA
// installs. This is a fallback for browsers/WebViews (WhatsApp on iOS,
// Instagram, etc.) that silently drop our SameSite=None auth cookie. Every
// outgoing request attaches it as an `Authorization: Bearer <token>` header;
// the backend accepts either the cookie or this header.
const TOKEN_KEY = "scs_access_token";

export function getStoredToken() {
    try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}

export function setStoredToken(token) {
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch { /* private mode / storage disabled — cookie will still work */ }
}

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
    timeout: 20000,
});

api.interceptors.request.use((cfg) => {
    const t = getStoredToken();
    if (t) {
        cfg.headers = cfg.headers || {};
        // Don't clobber a caller-supplied Authorization header.
        if (!cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
    }
    return cfg;
});

// Retry once on transient network errors — but ONLY for idempotent requests
// (avoid double-creating resources if the server actually received the first POST)
const SAFE_METHODS = new Set(["get", "head", "options"]);
const SAFE_RETRY_URLS = [
    "/auth/login", "/auth/me", "/auth/logout",
    // Clock-in / clock-out are safely retry-able: the server treats a repeat
    // call as a no-op (returns 400 "already clocked in / out"), so a stale
    // mobile connection won't accidentally create duplicate entries.
    "/clock-in", "/clock-out",
];
api.interceptors.response.use(
    (r) => r,
    async (err) => {
        const cfg = err?.config || {};
        const transient =
            !err.response &&
            (err.code === "ERR_NETWORK" || err.message === "Network Error" || err.code === "ECONNABORTED");
        const method = (cfg.method || "get").toLowerCase();
        const url = cfg.url || "";
        const isSafe = SAFE_METHODS.has(method) || SAFE_RETRY_URLS.some(u => url.endsWith(u));
        if (transient && isSafe && !cfg.__retried) {
            cfg.__retried = true;
            await new Promise(r => setTimeout(r, 800));
            return api.request(cfg);
        }
        return Promise.reject(err);
    }
);

export function formatApiError(err) {
    if (!err) return "Something went wrong";
    // No response = network / offline / CORS / server unreachable
    if (!err.response) {
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
            return "You appear to be offline. Reconnect and try again.";
        }
        return "Can't reach the server. Check your connection and tap try again.";
    }
    const detail = err.response.data?.detail;
    if (detail == null) return err.message || "Something went wrong";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    }
    // Handle structured error dicts from our own routes. We emit shapes like
    // `{error: 'checklist_incomplete', message: '…', missing: [...]}` so prefer
    // a human-readable `message` or legacy `msg` over dumping the raw object.
    if (detail && typeof detail === "object") {
        if (typeof detail.message === "string") return detail.message;
        if (typeof detail.msg === "string") return detail.msg;
        if (typeof detail.error === "string") return detail.error;
    }
    return String(detail);
}
